/**
 * Identity field for invite dialogs (#287).
 *
 * Two modes:
 *  - "username" (default): typeahead against /api/users/search. Returns
 *    `{ user_id }` payload when a result is picked. Username is the
 *    public handle (GitHub-style); the server resolves the on-file
 *    email and never returns it to the caller.
 *  - "email": legacy fallback for inviting an unregistered user.
 *    Returns `{ email }` payload.
 *
 * Usage:
 *   const field = createInviteIdentityField({
 *     parent: dlg,                   // appended after this element
 *     existingMemberIds: [...],      // filtered out of typeahead results
 *   });
 *   // later, on submit:
 *   const ident = field.getIdentity();
 *   if (!ident) { return; }   // invalid — abort send
 *   const payload = { ...ident, role: chosenRole };
 */

import { lubooRequest } from '../mixins/data-channel.mixin.js';

function escape(s) {
  return String(s ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function displayName(u) {
  const parts = [u.first_name, u.last_name].filter(Boolean);
  if (parts.length) return parts.join(' ');
  return u.username;
}

export function createInviteIdentityField({ parent, existingMemberIds = [] }) {
  const wrap = document.createElement('div');
  wrap.className = 'invite-identity-field';

  // ── Username mode ────────────────────────────────────────────
  const usernameBox = document.createElement('div');
  usernameBox.className = 'invite-identity-username-box';

  const usernameLabel = document.createElement('label');
  usernameLabel.className = 'invite-identity-label';
  const usernameSpan = document.createElement('span');
  usernameSpan.textContent = 'Username';
  usernameLabel.appendChild(usernameSpan);

  const inputWrap = document.createElement('div');
  inputWrap.className = 'invite-identity-input-wrap';

  const usernameInput = document.createElement('input');
  usernameInput.type = 'text';
  usernameInput.className = 'dialog-input';
  usernameInput.autocomplete = 'off';
  usernameInput.placeholder = 'Start typing a username…';
  inputWrap.appendChild(usernameInput);

  const dropdown = document.createElement('div');
  dropdown.className = 'invite-identity-dropdown';
  dropdown.hidden = true;
  inputWrap.appendChild(dropdown);

  usernameLabel.appendChild(inputWrap);
  usernameBox.appendChild(usernameLabel);

  const emailToggle = document.createElement('a');
  emailToggle.href = '#';
  emailToggle.className = 'invite-identity-email-toggle';
  emailToggle.textContent = 'Invite by email instead →';
  usernameBox.appendChild(emailToggle);

  wrap.appendChild(usernameBox);

  // ── Email mode ───────────────────────────────────────────────
  const emailBox = document.createElement('div');
  emailBox.className = 'invite-identity-email-box';
  emailBox.hidden = true;

  const emailLabel = document.createElement('label');
  emailLabel.className = 'invite-identity-label';
  const emailSpan = document.createElement('span');
  emailSpan.textContent = 'Email';
  emailLabel.appendChild(emailSpan);
  const emailInput = document.createElement('input');
  emailInput.type = 'email';
  emailInput.className = 'dialog-input';
  emailInput.placeholder = 'name@example.com';
  emailLabel.appendChild(emailInput);
  emailBox.appendChild(emailLabel);

  const usernameToggle = document.createElement('a');
  usernameToggle.href = '#';
  usernameToggle.className = 'invite-identity-email-toggle';
  usernameToggle.textContent = '← Search registered users instead';
  emailBox.appendChild(usernameToggle);

  wrap.appendChild(emailBox);

  parent.appendChild(wrap);

  // ── State ────────────────────────────────────────────────────
  const memberSet = new Set(existingMemberIds);
  let mode = 'username';
  let selectedUser = null; // {user_id, username, ...} once picked
  let debounceTimer = null;
  let lastFetchToken = 0;
  let activeIndex = -1;
  let resultItems = []; // currently-rendered user objects
  let blurHideTimer = null;

  function setMode(next) {
    mode = next;
    if (mode === 'username') {
      usernameBox.hidden = false;
      emailBox.hidden = true;
      usernameInput.focus();
    } else {
      usernameBox.hidden = true;
      emailBox.hidden = false;
      emailInput.focus();
    }
  }

  emailToggle.addEventListener('click', (e) => {
    e.preventDefault();
    setMode('email');
  });
  usernameToggle.addEventListener('click', (e) => {
    e.preventDefault();
    setMode('username');
  });

  function hideDropdown() {
    dropdown.hidden = true;
    activeIndex = -1;
  }

  function renderDropdown() {
    if (!resultItems.length) {
      dropdown.innerHTML = `
        <div class="invite-identity-empty">
          No users matching "${escape(usernameInput.value.trim())}".
          <a href="#" data-invite-identity-action="switch-email">
            Invite by email
          </a>
          instead?
        </div>
      `;
      dropdown.hidden = false;
      return;
    }
    dropdown.innerHTML = resultItems
      .map(
        (u, i) => `
          <div class="invite-identity-result${i === activeIndex ? ' is-active' : ''}"
               data-invite-identity-action="pick"
               data-user-id="${escape(u.user_id)}"
               role="option">
            <div class="invite-identity-avatar">
              <topographic-identicon-or-image
                username="${escape(u.username)}"
                ${u.avatar_url ? `url="${escape(u.avatar_url)}"` : ''}
                num-peaks="4" num-contours="12"
                peak-height-max="0.9" peak-spread="23"
                peak-spacing="1" stroke-width="0.9"
                show-peaks="false"
                bg-color="var(--panel-color)"
                line-color="var(--text-color)"
                accent-color="var(--highlight-color)">
              </topographic-identicon-or-image>
            </div>
            <div class="invite-identity-result-text">
              <div class="invite-identity-result-name">${escape(displayName(u))}</div>
              <div class="invite-identity-result-handle">@${escape(u.username)}</div>
            </div>
          </div>
        `
      )
      .join('');
    dropdown.hidden = false;
  }

  async function runSearch(q) {
    const token = ++lastFetchToken;
    try {
      const data = await lubooRequest(
        'luboo:api',
        'user_search_request',
        { q, limit: 10 },
      );
      if (token !== lastFetchToken) return; // outdated; ignore
      resultItems = (Array.isArray(data) ? data : []).filter(
        (u) => !memberSet.has(u.user_id)
      );
      activeIndex = resultItems.length ? 0 : -1;
      renderDropdown();
    } catch {
      // network error — silently leave dropdown closed
    }
  }

  usernameInput.addEventListener('input', () => {
    selectedUser = null;
    usernameInput.classList.remove('is-picked');
    const q = usernameInput.value.trim();
    if (debounceTimer) clearTimeout(debounceTimer);
    if (q.length < 2) {
      hideDropdown();
      return;
    }
    debounceTimer = setTimeout(() => runSearch(q), 200);
  });

  usernameInput.addEventListener('focus', () => {
    if (blurHideTimer) {
      clearTimeout(blurHideTimer);
      blurHideTimer = null;
    }
    if (resultItems.length && !selectedUser) dropdown.hidden = false;
  });

  usernameInput.addEventListener('blur', () => {
    // Delay so click on a result still registers.
    blurHideTimer = setTimeout(() => hideDropdown(), 150);
  });

  usernameInput.addEventListener('keydown', (e) => {
    if (dropdown.hidden || !resultItems.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = (activeIndex + 1) % resultItems.length;
      renderDropdown();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex =
        (activeIndex - 1 + resultItems.length) % resultItems.length;
      renderDropdown();
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0) {
        e.preventDefault();
        pick(resultItems[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      hideDropdown();
    }
  });

  dropdown.addEventListener('mousedown', (e) => {
    // mousedown so the input doesn't blur first.
    const empty = e.target.closest('[data-invite-identity-action="switch-email"]');
    if (empty) {
      e.preventDefault();
      hideDropdown();
      setMode('email');
      return;
    }
    const row = e.target.closest('[data-invite-identity-action="pick"]');
    if (!row) return;
    e.preventDefault();
    const uid = row.getAttribute('data-user-id');
    const u = resultItems.find((x) => x.user_id === uid);
    if (u) pick(u);
  });

  function pick(u) {
    selectedUser = u;
    usernameInput.value = `@${u.username}`;
    usernameInput.classList.add('is-picked');
    hideDropdown();
  }

  return {
    focus() {
      if (mode === 'username') usernameInput.focus();
      else emailInput.focus();
    },
    /**
     * Returns either `{user_id}` or `{email}` for the invite payload,
     * or null if the field hasn't been filled in correctly.
     */
    getIdentity() {
      if (mode === 'email') {
        const email = emailInput.value.trim();
        if (!email) return null;
        return { email };
      }
      if (selectedUser) return { user_id: selectedUser.user_id };
      return null;
    },
    /**
     * Returns a human-readable error if the field is in an invalid state.
     */
    validate() {
      if (mode === 'email') {
        if (!emailInput.value.trim()) return 'Please enter an email address.';
        return null;
      }
      if (!selectedUser) {
        return 'Please pick a user from the list, or use "Invite by email instead".';
      }
      return null;
    },
  };
}
